<script setup lang="ts">
import type { EnqueteInput } from '@/types/schema.generated'
import {
  useIssueEnqueteAnswersCsvFileDownloadUrlMutation,
  useUpdateEnqueteMutation,
  type EnqueteEditPageContentEnquetePayloadFragment,
  type EnqueteEditPageContentUserVariableDefinitionFragment,
} from './EnqueteEditPageContent.generated'
import { useAlert } from '@/libs/alertPortal'
import { computed } from 'vue'
import EnqueteForm from '@/features/enquete/components/EnqueteForm/EnqueteForm.vue'
import type { EnqueteFormType } from '@/features/enquete/components/EnqueteForm/formSchema'
import { nanoid } from 'nanoid'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import IconClipboard from '@/components/icons/IconClipboard.vue'
import IconFile from '@/components/icons/IconFile.vue'
import * as Sentry from '@sentry/vue'

const props = defineProps<{
  payload: EnqueteEditPageContentEnquetePayloadFragment
  userVariableDefinitions: EnqueteEditPageContentUserVariableDefinitionFragment[]
}>()

const emit = defineEmits<{
  (e: 'updated'): void
}>()

const alert = useAlert()

const initialForm = computed<EnqueteFormType>(() => {
  return {
    title: props.payload.enquete.title,
    questions: props.payload.enquete.questions.map(question => ({
      key: nanoid(),
      guid: question.guid,
      questionText: question.questionText,
      userVariableDefinitionGUID: question.userVariableDefinition.guid,
      isAnswerRequired: question.isAnswerRequired,
    })),
    answeredChatMessage: props.payload.enquete.answeredChatMessage
      ? {
          body: props.payload.enquete.answeredChatMessage.body,
          selections:
            props.payload.enquete.answeredChatMessage.selections ?? undefined,
        }
      : undefined,
  }
})

const updateMutation = useUpdateEnqueteMutation()

const handleSubmit = async (input: EnqueteInput) => {
  try {
    await updateMutation.mutate({
      input: {
        guid: props.payload.enquete.guid,
        enquete: input,
      },
    })

    alert.success('アンケートを更新しました')
    emit('updated')
  } catch (error) {
    console.error(error)
    Sentry.captureException(error)
    alert.error('エラーが発生しました')
  }
}

const { answerEnqueteLiffAppPageUrl } = props.payload.enquete

const handleClickCopyAnswerPageUrlButton = () => {
  if (answerEnqueteLiffAppPageUrl == null) {
    throw new Error('回答ページ URL が存在しません')
  }
  window.navigator.clipboard.writeText(answerEnqueteLiffAppPageUrl)
  alert.success('URL をコピーしました')
}

const issueEnqueteAnswersCsvFileDownloadUrlMutation =
  useIssueEnqueteAnswersCsvFileDownloadUrlMutation()

const { answerCount } = props.payload.enquete

const handleClickDownloadAnswersCsvFile = async () => {
  const res = await issueEnqueteAnswersCsvFileDownloadUrlMutation.mutate({
    input: {
      enqueteGUID: props.payload.enquete.guid,
    },
  })
  if (!res) {
    console.error('res is undefined')
    alert.error('エラーが発生しました')
    return
  }
  if (res.errors) {
    console.error(res.errors)
    alert.error('エラーが発生しました')
    return
  }
  if (!res.data) {
    console.error('res.data is undefined')
    alert.error('エラーが発生しました')
    return
  }

  const { downloadURL } = res.data.issueEnqueteAnswersCsvFileDownloadURL

  window.open(downloadURL, '_blank')
}
</script>

<template>
  <div :class="$style.container">
    <section :class="$style.section">
      <h1>アンケート 詳細ページ</h1>
      <EnqueteForm
        :initial-form="initialForm"
        :user-variable-definitions="userVariableDefinitions"
        @submit="handleSubmit"
        :class="$style.form"
      />
    </section>
    <section :class="$style.section">
      <h1>LINE ユーザー向け 回答ページ URL</h1>
      <div :class="$style.answerPageUrlContainer">
        <template v-if="answerEnqueteLiffAppPageUrl">
          <DolphinTextInput
            readonly
            v-model="answerEnqueteLiffAppPageUrl"
            :class="$style.urlInput"
          />
          <FlatButton
            variant="base"
            size="small"
            @click="handleClickCopyAnswerPageUrlButton"
          >
            <template #icon><IconClipboard /></template>コピー
          </FlatButton>
        </template>
        <template v-else>
          <p>※ 回答ページを作るには LIFF 設定を行ってください</p>
        </template>
      </div>
    </section>
    <section :class="$style.section">
      <h1>回答結果</h1>
      <div :class="$style.answersContainer">
        <p>回答者数： {{ answerCount.toLocaleString('ja-JP') }}名</p>
        <div :class="$style.answersContainerButtonWrapper">
          <FlatButton
            variant="base"
            size="medium"
            @click="handleClickDownloadAnswersCsvFile"
          >
            <template #icon><IconFile /></template>回答結果 CSV ファイル
            ダウンロード
          </FlatButton>
        </div>
      </div>
    </section>
  </div>
</template>

<style module lang="scss">
.container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section {
  background-color: $color-surface-primary;
  padding: 24px;
  border-radius: 16px;
}

.form {
  margin-top: 16px;
}

.answerPageUrlContainer {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.urlInput {
  flex: 1;
  height: 40px;
  font-size: 14px;
}

.answersContainer {
  margin-top: 16px;
}

.answersContainerButtonWrapper {
  margin-top: 8px;
}
</style>
